import {
  useRouteMatch,
  useHistory,
  useLocation,
  useParams
} from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'
import { deserializeQueryString } from './urlManager'
import { useSelectionFilterBuilder } from '../hooks/use-selection-filter-builder'
import { useBulkOperations } from '../hooks/use-bulk-operations'

function getDisplayName(Component) {
  return Component.displayName || Component.name || 'Component'
}

export function withHistory(Component) {
  const WrappedComponent = (props) => (
    <Component {...props} history={useHistory()} />
  )

  WrappedComponent.displayName = `withHistory(${getDisplayName(Component)})`

  return WrappedComponent
}

export function withParams(Component) {
  const WrappedComponent = (props) => (
    <Component {...props} params={useParams()} />
  )

  WrappedComponent.displayName = `withParams(${getDisplayName(Component)})`

  return WrappedComponent
}

export function withLocation(Component) {
  const WrappedComponent = (props) => {
    const location = useLocation()

    return (
      <Component
        {...props}
        location={location}
        urlQuery={deserializeQueryString(location.search)}
      />
    )
  }

  WrappedComponent.displayName = `withLocation(${getDisplayName(Component)})`

  return WrappedComponent
}

export function withRouteMatch(Component) {
  const WrappedComponent = (props) => (
    <Component {...props} match={useRouteMatch()} />
  )

  WrappedComponent.displayName = `withRouteMatch(${getDisplayName(Component)})`

  return WrappedComponent
}

export function withUseIntercom(Component) {
  const WrappedComponent = (props) => (
    <Component {...props} useIntercom={useIntercom()} />
  )

  WrappedComponent.displayName = `withUseIntercom(${getDisplayName(Component)})`

  return WrappedComponent
}

export function withRouter(Component) {
  const WrappedComponent = (props) => {
    const location = useLocation()

    return (
      <Component
        {...props}
        history={useHistory()}
        location={location}
        match={useRouteMatch()}
        params={useParams()}
        urlQuery={deserializeQueryString(location.search)}
      />
    )
  }

  WrappedComponent.displayName = `withRouter(${getDisplayName(Component)})`

  return WrappedComponent
}

export const withBulkOperations = (Component) => {
  return function AuthenticatedComponent(props) {
    const bulkOperation = useBulkOperations()
    const buildFiltersOptions = useSelectionFilterBuilder()

    return (
      <Component
        {...props}
        bulkOperation={bulkOperation}
        buildFiltersOptions={buildFiltersOptions}
      />
    )
  }
}
